import dayjs, {Dayjs, duration} from 'dayjs';
import {secondsInAMinute} from '../settings/alert_alarm/constants';
import {Assignee, GroupListProps, OptionsProp} from '../../constants/types';
import {
  AvailableAssignee,
  ChecklistItem,
  CurrentTask,
  ProcessTask
} from './types';
import {ProcessCode} from '../processes/types';
import permissionsInfo from '../../constants/permissionsInfo';

dayjs.extend(duration);
const DOT = '.';
const REG_EXP = /(.+)([\W])$/;
const runningDelay = 20000;
const notRunningDelay = 2000;

type shouldShowClaimButtonProps = {
  taskGroups?: GroupListProps[],
  userGroups?: Nullable<string[]>,
  userId?: Nullable<number>,
  taskAssignee: Nullable<Assignee>,
  isRunning: boolean
}

export const currentUserIsAssignee = (
    taskAssignee: Nullable<Assignee>,
    userId?: Nullable<number>
) => {
  return !!taskAssignee && userId === taskAssignee?.id;
};

export const shouldShowClaimButton = ({
  taskGroups, userGroups, userId, taskAssignee, isRunning
}: shouldShowClaimButtonProps): boolean => {
  if (!isRunning) {
    return false;
  }

  const hasGroupIntersection =
    !!(taskGroups?.filter((group) => userGroups?.includes(group.slug)).length);

  const isAssignee = currentUserIsAssignee(taskAssignee, userId);

  return hasGroupIntersection && !isAssignee;
};

export const formatChecklist = (
    checklist: Nullable<ChecklistItem[]>
): ChecklistItem[] => {
  if (!checklist) {
    return [];
  }

  return checklist.map((item) => {
    const {message} = item;
    const formattedMessage = message.replace(REG_EXP, '$1.');
    const lastSymbol = formattedMessage[formattedMessage.length - 1];

    return {
      ...item,
      message: lastSymbol === DOT ? formattedMessage : `${formattedMessage}.`
    };
  });
};


const sortAvailableAssigneeOptions = ({label: label1}, {label: label2}) => {
  if (label1 < label2) {
    return -1;
  }

  if (label1 > label2) {
    return 1;
  }

  return 0;
};

export const formatAssigneesToOptions = (
    availableAssignees: AvailableAssignee[] = []
): OptionsProp[] => {
  if (!availableAssignees?.length) {
    return [];
  }

  return availableAssignees.map(({id, firstName, lastName, email}) => ({
    value: `${id}`,
    label: `${firstName} ${lastName} (${email})`
  })).sort(sortAvailableAssigneeOptions);
};

export const createArraysOfDuplicates = (
    array: ProcessTask[], field: string
): ProcessTask[][] => {
  return [...array.reduce((a, b) =>
    a.set(b[field], (a.get(b[field]) || []).concat(b)),
  new Map()).values()];
};

export const calculateAndFormatTime = (timeInSeconds: number|Dayjs) => {
  const minutes = Math.floor(Number(timeInSeconds) / secondsInAMinute);

  return `${minutes}m`;
};

export const getRefreshDelay = (tasks: ProcessTask[]) => {
  if (tasks.find(({completedAt}) => !completedAt)) {
    return runningDelay;
  }
  return notRunningDelay;
};

export const getAvailableCurrentTasks = (
    tasks: CurrentTask[],
    userGroups,
    permissions
): CurrentTask[]=> {
  if (!tasks?.length) {
    return [];
  }

  return tasks.filter(({status, groups: taskGroups}) => {
    const isRunning = status?.code === ProcessCode.RUNNING;
    const hasViewPermission =
      permissions?.includes(...permissionsInfo.tasks.view);

    const hasGroupIntersection = taskGroups
        .some(({slug}) => userGroups?.includes(slug));

    return isRunning && (hasGroupIntersection || hasViewPermission);
  });
};

export const getUniqueElements = (arr: ProcessTask[]) => {
  return [...new Map(arr.map((item) => [item['id'], item])).values()];
};
